import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { confirmSession, confirmGiftSession } from '../../services/paymentService';

// Stripe redirects here after checkout with ?session_id=cs_xxx -- for
// BOTH a regular purchase and a gift purchase, since they share the same
// success URL. We try the gift confirmation first (harmless 400 if it
// wasn't a gift), then fall back to the regular one.
const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const [status, setStatus] = useState('checking'); // checking | active | pending | error
  const [purchaseType, setPurchaseType] = useState(null); // 'plan' | 'upgrade' | 'scan_pack' | 'gift'
  const [giftCode, setGiftCode] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const confirm = async () => {
      try {
        const giftData = await confirmGiftSession(sessionId);
        setPurchaseType('gift');
        setGiftCode(giftData.giftCode || null);
        setStatus(giftData.activated ? 'active' : 'pending');
        return;
      } catch (err) {
        // Not a gift session -- fall through to the regular confirmation
      }

      try {
        const data = await confirmSession(sessionId);
        setPurchaseType(data.purchaseType || null);
        setStatus(data.activated ? 'active' : 'pending');
      } catch (err) {
        setStatus('error');
      }
    };
    confirm();
  }, [sessionId]);

  const successText = () => {
    if (purchaseType === 'gift') return "Your gift is ready — share the code below with whoever you're gifting a cookbook to.";
    if (purchaseType === 'scan_pack') return 'Your scan pack has been added to your cookbook.';
    if (purchaseType === 'upgrade') return 'Your cookbook has been upgraded to Community.';
    return 'Your plan is active. You can now invite members.'; // 'plan' or unknown
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(giftCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {status === 'checking' && (
          <>
            <h2 style={styles.title}>Confirming your payment...</h2>
            <p style={styles.text}>This usually takes just a second.</p>
          </>
        )}
        {status === 'active' && (
          <>
            <h2 style={styles.titleSuccess}>🎉 Payment successful!</h2>
            <p style={styles.text}>{successText()}</p>
            {purchaseType === 'gift' && giftCode && (
              <div style={styles.giftCodeBox}>
                <p style={styles.giftCodeText}>{giftCode}</p>
                <button onClick={handleCopy} style={styles.copyBtn}>
                  {copied ? '✓ Copied!' : 'Copy Code'}
                </button>
                <p style={styles.giftCodeHint}>
                  We also emailed this to you. Valid for one year, redeemable once.
                </p>
              </div>
            )}
            <button onClick={() => navigate('/dashboard')} style={styles.button}>Go to Dashboard</button>
          </>
        )}
        {status === 'pending' && (
          <>
            <h2 style={styles.title}>Payment still processing</h2>
            <p style={styles.text}>Give it a moment, then check your dashboard.</p>
            <button onClick={() => navigate('/dashboard')} style={styles.button}>Go to Dashboard</button>
          </>
        )}
        {status === 'error' && (
          <>
            <h2 style={styles.title}>Couldn't confirm payment</h2>
            <p style={styles.text}>
              If you completed checkout, your dashboard should update shortly. If not, please try again.
            </p>
            <button onClick={() => navigate('/dashboard')} style={styles.button}>Go to Dashboard</button>
          </>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f5f5f5',
  },
  card: {
    backgroundColor: 'white',
    padding: '40px',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    textAlign: 'center',
    maxWidth: '420px',
  },
  title: { color: '#333', marginBottom: '10px' },
  titleSuccess: { color: '#27ae60', marginBottom: '10px' },
  text: { color: '#666', marginBottom: '20px' },
  button: {
    padding: '12px 24px',
    backgroundColor: '#e67e22',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  giftCodeBox: {
    backgroundColor: '#fdf0e3',
    borderRadius: '10px',
    padding: '20px',
    marginBottom: '20px',
  },
  giftCodeText: {
    fontSize: '22px',
    fontWeight: '700',
    letterSpacing: '2px',
    color: '#e67e22',
    marginBottom: '12px',
  },
  copyBtn: {
    padding: '8px 20px',
    backgroundColor: 'white',
    color: '#e67e22',
    border: '1px solid #e67e22',
    borderRadius: '8px',
    fontWeight: '600',
    cursor: 'pointer',
    fontSize: '13px',
  },
  giftCodeHint: { color: '#a05a1a', fontSize: '12px', marginTop: '12px', marginBottom: 0 },
};

export default PaymentSuccess;
